import React, { useMemo, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Container,
  FormControl,
  FormLabel,
  Flex,
  Grid,
  GridItem,
  Heading,
  HStack,
  Input,
  Link,
  Select,
  SimpleGrid,
  Stack,
  Textarea,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import { API_BASE_URL } from '../services/apiClient';
import { APP_DESCRIPTION } from '../config/runtime';
import Logo from './Logo';

const contentByLang = {
  en: {
    navFeatures: 'Features',
    navHow: 'How it works',
    navWhy: 'Why now',
    navContact: 'Contact',
    login: 'Log in',
    register: 'Create account',
    heroTitle: 'Agile threat modeling to make security decisions before writing code.',
    heroDescription: `${APP_DESCRIPTION} Embed security from design time, reduce remediation costs, and standardize risk conversations across product and engineering teams.`,
    heroPrimary: 'Create account',
    heroSecondary: 'See platform',
    features: [
      {
        title: 'Guided STRIDE',
        body: 'Identify threats systematically by type and context, without relying on tribal knowledge or one-off workshops.',
      },
      {
        title: 'OWASP Risk Rating',
        body: 'Score likelihood and impact to prioritize mitigations with clear severity and shared language across teams.',
      },
      {
        title: 'Control mapping',
        body: 'Map remediations to ASVS, MASVS, NIST, and other standards to make outputs auditable and execution-ready.',
      },
      {
        title: 'Collaborative operations',
        body: 'Share models, organize projects, and preserve decision traceability across teams shipping software continuously.',
      },
    ],
    stepsTitle: 'How it works',
    stepsSubtitle: 'A repeatable workflow to add security without slowing teams down.',
    steps: [
      'Upload a diagram or describe your architecture.',
      'Classify threats with STRIDE and technical context.',
      'Assess severity with OWASP Risk Rating.',
      'Turn mitigations into actionable backlog work.',
    ],
    highlights: [
      'Real shift-left for product and architecture',
      'Exportable evidence for audit and governance',
      'Open source foundation, enterprise-ready operations',
    ],
    whyBadge: 'Why now',
    whyTitle: 'Detect earlier, fix cheaper, and document better.',
    whyBody:
      'Modern organizations cannot wait for late security testing to discover design risks. Threat modeling works best when embedded into product, architecture, and engineering workflows.',
    whyCards: [
      {
        title: 'Real shift-left',
        body: 'Evaluate risk when architecture, API, and flow decisions are still easy and low-cost to change.',
      },
      {
        title: 'Governance and evidence',
        body: 'Keep complete traceability for threats, severity, controls, and decisions for compliance and audits.',
      },
      {
        title: 'Scales across teams',
        body: 'Avoid isolated sessions. Share context and repeat the process across products and squads.',
      },
    ],
    ctaBadge: 'Start today',
    ctaTitle:
      'Create your account and start threat modeling with a workflow that is simple, collaborative, and scalable.',
    ctaBody:
      'From sign-up to your first model in minutes, with your team aligned around the same risk language.',
    ctaSecondary: 'I already have an account',
    poweredBadge: 'Open Source Foundation',
    poweredTitle: 'Powered by Open Source',
    poweredBody:
      'Threat Modeling Studio is built on top of TZU, an open-source threat modeling project.',
    poweredLink: 'Visit tzusecurity.com',
    contactTitle: 'Contact us',
    contactBody:
      'Share your use case and we will help you design a threat modeling workflow for your organization.',
    contactName: 'Name',
    contactEmail: 'Work email',
    contactCompany: 'Company',
    contactSource: 'How did you find us?',
    contactSourcePlaceholder: 'Select one',
    contactSourceOptions: [
      'Google Search',
      'LinkedIn',
      'GitHub / Open Source',
      'Referral',
      'Conference / Meetup',
      'Other',
    ],
    contactMessage: 'Message',
    contactSubmit: 'Send message',
    contactSubmitting: 'Sending...',
    contactSuccess: 'Message sent successfully. Our team will contact you soon.',
    contactError: 'We could not send your message. Please try again in a moment.',
    emailSubject: 'Threat Modeling Studio - Contact request',
    footerCopyright: 'All rights reserved.',
  },
  es: {
    navFeatures: 'Características',
    navHow: 'Cómo funciona',
    navWhy: 'Por qué ahora',
    navContact: 'Contacto',
    login: 'Iniciar sesión',
    register: 'Crear cuenta',
    heroTitle: 'Modelado de amenazas ágil para decidir seguridad antes de escribir código.',
    heroDescription: `${APP_DESCRIPTION} Integra seguridad desde el diseño, reduce el costo de corrección y estandariza la conversación de riesgos en equipos de producto y desarrollo.`,
    heroPrimary: 'Crear cuenta',
    heroSecondary: 'Ver plataforma',
    features: [
      {
        title: 'STRIDE guiado',
        body: 'Identifica amenazas sistemáticamente por tipo y contexto para no depender de memoria individual ni workshops improvisados.',
      },
      {
        title: 'OWASP Risk Rating',
        body: 'Calcula probabilidad e impacto para priorizar mitigaciones con severidad clara y lenguaje común entre seguridad y producto.',
      },
      {
        title: 'Mapeo a controles',
        body: 'Alinea remediaciones con ASVS, MASVS, NIST y otros estándares para que el resultado sea accionable y auditable.',
      },
      {
        title: 'Operación colaborativa',
        body: 'Comparte modelos, organiza proyectos y mantén trazabilidad de decisiones en equipos que construyen software de forma continua.',
      },
    ],
    stepsTitle: 'Cómo funciona',
    stepsSubtitle: 'Un flujo repetible para incorporar seguridad sin frenar al equipo.',
    steps: [
      'Sube un diagrama o describe tu arquitectura.',
      'Clasifica amenazas con STRIDE y contexto técnico.',
      'Valora severidad con OWASP Risk Rating.',
      'Convierte mitigaciones en trabajo accionable para el backlog.',
    ],
    highlights: [
      'Shift-left real para arquitectura y producto',
      'Evidencia exportable para auditoría y gobernanza',
      'Open source como base, operación lista para empresas',
    ],
    whyBadge: 'Por qué ahora',
    whyTitle: 'Detecta antes, corrige más barato y documenta mejor.',
    whyBody:
      'Las organizaciones ágiles no pueden esperar al ethical hacking para descubrir riesgos de diseño. El modelado de amenazas funciona mejor cuando vive dentro del flujo de producto, arquitectura y desarrollo.',
    whyCards: [
      {
        title: 'Shift-left real',
        body: 'Evalúa riesgos cuando todavía es barato cambiar decisiones de arquitectura, API o flujos críticos.',
      },
      {
        title: 'Gobernanza y evidencia',
        body: 'Mantén trazabilidad de amenazas, severidad, controles y decisiones para compliance y auditoría.',
      },
      {
        title: 'Escalable para equipos',
        body: 'Evita depender de sesiones aisladas. Comparte contexto y repite el proceso entre productos y squads.',
      },
    ],
    ctaBadge: 'Empieza hoy',
    ctaTitle:
      'Crea tu cuenta y empieza a modelar amenazas con un flujo más simple, colaborativo y escalable.',
    ctaBody:
      'En un clic entras al registro y en minutos puedes crear tu primer threat model junto a tu equipo.',
    ctaSecondary: 'Ya tengo cuenta',
    poweredBadge: 'Base Open Source',
    poweredTitle: 'Impulsado por Open Source',
    poweredBody:
      'Threat Modeling Studio está construido sobre TZU, un proyecto open source de threat modeling.',
    poweredLink: 'Visitar tzusecurity.com',
    contactTitle: 'Contáctanos',
    contactBody:
      'Cuéntanos tu caso de uso y te ayudamos a diseñar un flujo de modelado de amenazas para tu organización.',
    contactName: 'Nombre',
    contactEmail: 'Email corporativo',
    contactCompany: 'Empresa',
    contactSource: '¿Por dónde nos encontraste?',
    contactSourcePlaceholder: 'Selecciona una opción',
    contactSourceOptions: [
      'Google',
      'LinkedIn',
      'GitHub / Open Source',
      'Recomendación',
      'Evento / Meetup',
      'Otro',
    ],
    contactMessage: 'Mensaje',
    contactSubmit: 'Enviar mensaje',
    contactSubmitting: 'Enviando...',
    contactSuccess: 'Mensaje enviado correctamente. Nuestro equipo te contactará pronto.',
    contactError: 'No pudimos enviar tu mensaje. Inténtalo nuevamente en un momento.',
    emailSubject: 'Threat Modeling Studio - Solicitud de contacto',
    footerCopyright: 'Todos los derechos reservados.',
  },
};

const LandingPage = () => {
  const [language, setLanguage] = useState('en');
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    company: '',
    source: '',
    website: '',
    message: '',
  });
  const [isSubmittingContact, setIsSubmittingContact] = useState(false);
  const toast = useToast();
  const copy = useMemo(() => contentByLang[language], [language]);
  const currentYear = new Date().getFullYear();

  const handleContactSubmit = async (event) => {
    event.preventDefault();

    setIsSubmittingContact(true);
    try {
      const response = await fetch(`${API_BASE_URL}/v1/public/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...contactForm,
          language,
        }),
      });

      if (!response.ok) {
        throw new Error('Contact submission failed');
      }

      setContactForm({
        name: '',
        email: '',
        company: '',
        source: '',
        website: '',
        message: '',
      });

      toast({
        title: copy.contactSuccess,
        status: 'success',
        duration: 3500,
        isClosable: true,
        position: 'top-right',
      });
    } catch (error) {
      toast({
        title: copy.contactError,
        status: 'error',
        duration: 4200,
        isClosable: true,
        position: 'top-right',
      });
    } finally {
      setIsSubmittingContact(false);
    }
  };

  const onFormFieldChange = (field) => (event) => {
    setContactForm((previous) => ({
      ...previous,
      [field]: event.target.value,
    }));
  };

  return (
    <Box bg="gray.900" color="white" minH="100vh" textAlign="left">
      <Box
        bgGradient="radial(circle at top, rgba(79,70,229,0.28), rgba(2,6,23,0) 36%), linear-gradient(180deg, #0b1120 0%, #020617 100%)"
        borderBottom="1px solid rgba(148,163,184,0.12)"
      >
        <Container maxW="7xl" px={{ base: 5, md: 8 }}>
          <Flex py={5} align="center" justify="space-between" gap={4} wrap="wrap">
            <HStack spacing={3}>
              <Logo size="52px" cropScale={1.55} imageFilter="brightness(0) invert(1)" />
              <VStack spacing={0} align="flex-start" lineHeight="1.05">
                <Text fontWeight="800" color="white" letterSpacing="tight">
                  ThreatModeling
                  <Text as="span" color="teal.300">.studio</Text>
                </Text>
                <Badge
                  colorScheme="teal"
                  variant="subtle"
                  px={2}
                  py={0.5}
                  borderRadius="full"
                  fontSize="0.6em"
                  alignSelf="flex-start"
                  mt={0.5}
                >
                  Beta
                </Badge>
              </VStack>
            </HStack>

            <HStack spacing={5} display={{ base: 'none', md: 'flex' }} color="gray.300" fontSize="sm">
              <Link href="#features">{copy.navFeatures}</Link>
              <Link href="#how">{copy.navHow}</Link>
              <Link href="#why">{copy.navWhy}</Link>
              <Link href="#contact">{copy.navContact}</Link>
            </HStack>

            <HStack spacing={3}>
              <Button
                size="sm"
                variant="outline"
                borderColor="whiteAlpha.400"
                color="white"
                _hover={{ bg: 'whiteAlpha.100' }}
                onClick={() => setLanguage((prev) => (prev === 'en' ? 'es' : 'en'))}
              >
                {language === 'en' ? 'ES' : 'EN'}
              </Button>
              <Button as={RouterLink} to="/login" variant="ghost" color="gray.200" _hover={{ bg: 'whiteAlpha.100' }}>
                {copy.login}
              </Button>
              <Button as={RouterLink} to="/login?tab=register" bg="teal.300" color="gray.900" _hover={{ bg: 'teal.200' }}>
                {copy.register}
              </Button>
            </HStack>
          </Flex>

          <Grid templateColumns={{ base: '1fr', lg: '1.15fr 0.85fr' }} gap={{ base: 10, lg: 16 }} py={{ base: 14, md: 24 }} alignItems="center">
            <GridItem>
              <VStack align="start" spacing={7}>
                <HStack spacing={3} wrap="wrap">
                  <Text px={3} py={1} borderRadius="full" bg="whiteAlpha.200" color="indigo.100" fontSize="sm" fontWeight="700">
                    STRIDE
                  </Text>
                  <Text px={3} py={1} borderRadius="full" bg="whiteAlpha.200" color="teal.200" fontSize="sm" fontWeight="700">
                    OWASP Risk Rating
                  </Text>
                  <Text px={3} py={1} borderRadius="full" bg="whiteAlpha.200" color="indigo.200" fontSize="sm" fontWeight="700">
                    AI Powered
                  </Text>
                </HStack>

                <Heading fontSize={{ base: '4xl', md: '6xl' }} lineHeight="0.95" letterSpacing="tight">
                  {copy.heroTitle}
                </Heading>

                <Text fontSize={{ base: 'lg', md: 'xl' }} color="gray.300" maxW="3xl">
                  {copy.heroDescription}
                </Text>

                <HStack spacing={4} wrap="wrap">
                  <Button
                    as={RouterLink}
                    to="/login?tab=register"
                    size="lg"
                    bgGradient="linear(to-r, indigo.500, teal.400)"
                    color="white"
                    _hover={{ bgGradient: 'linear(to-r, indigo.400, teal.300)' }}
                  >
                    {copy.heroPrimary}
                  </Button>
                  <Button as={RouterLink} to="/login" size="lg" variant="outline" borderColor="whiteAlpha.300" color="white" _hover={{ bg: 'whiteAlpha.100' }}>
                    {copy.heroSecondary}
                  </Button>
                </HStack>

                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} w="full">
                  {copy.highlights.map((item) => (
                    <Box
                      key={item}
                      p={4}
                      borderRadius="2xl"
                      bg="whiteAlpha.200"
                      color="white"
                      border="1px solid"
                      borderColor="whiteAlpha.300"
                      backdropFilter="blur(10px)"
                    >
                      <Text fontWeight="700">{item}</Text>
                    </Box>
                  ))}
                </SimpleGrid>
              </VStack>
            </GridItem>

            <GridItem id="how">
              <Box borderRadius="3xl" bg="rgba(15,23,42,0.82)" border="1px solid rgba(79,70,229,0.26)" p={{ base: 6, md: 8 }} boxShadow="0 20px 80px rgba(79,70,229,0.16)">
                <VStack align="start" spacing={5}>
                  <Text color="teal.200" fontSize="sm" fontWeight="700" textTransform="uppercase" letterSpacing="0.08em">
                    {copy.stepsTitle}
                  </Text>
                  <Heading size="lg">{copy.stepsSubtitle}</Heading>
                  <Stack spacing={4} w="full">
                    {copy.steps.map((step, index) => (
                      <HStack key={step} align="start" spacing={4}>
                        <Flex minW="34px" h="34px" borderRadius="full" bg="indigo.500" color="white" align="center" justify="center" fontWeight="800">
                          {index + 1}
                        </Flex>
                        <Text color="gray.100">{step}</Text>
                      </HStack>
                    ))}
                  </Stack>
                </VStack>
              </Box>
            </GridItem>
          </Grid>
        </Container>
      </Box>

      <Container id="features" maxW="7xl" px={{ base: 5, md: 8 }} py={{ base: 16, md: 24 }}>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
          {copy.features.map((feature) => (
            <Box key={feature.title} p={7} borderRadius="3xl" bg="gray.900" border="1px solid rgba(148,163,184,0.12)">
              <VStack align="start" spacing={3}>
                <Heading size="md">{feature.title}</Heading>
                <Text color="gray.200">{feature.body}</Text>
              </VStack>
            </Box>
          ))}
        </SimpleGrid>
      </Container>

      <Box id="why" borderTop="1px solid rgba(148,163,184,0.12)" borderBottom="1px solid rgba(148,163,184,0.12)" bg="gray.900">
        <Container maxW="7xl" px={{ base: 5, md: 8 }} py={{ base: 16, md: 20 }}>
          <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={10}>
            <Box>
              <Text color="purple.200" fontSize="sm" fontWeight="700" textTransform="uppercase" letterSpacing="0.08em" mb={3}>
                {copy.whyBadge}
              </Text>
              <Heading fontSize={{ base: '3xl', md: '4xl' }} mb={5}>
                {copy.whyTitle}
              </Heading>
              <Text color="gray.200" fontSize="lg">
                {copy.whyBody}
              </Text>
            </Box>
            <SimpleGrid columns={1} spacing={4}>
              {copy.whyCards.map((card) => (
                <Box key={card.title} p={5} borderRadius="2xl" bg="blackAlpha.300">
                  <Heading size="sm" mb={2}>{card.title}</Heading>
                  <Text color="gray.200">{card.body}</Text>
                </Box>
              ))}
            </SimpleGrid>
          </SimpleGrid>
        </Container>
      </Box>

      <Container maxW="7xl" px={{ base: 5, md: 8 }} pt={{ base: 16, md: 24 }}>
        <Box
          p={{ base: 8, md: 10 }}
          borderRadius="3xl"
          bg="gray.900"
          bgGradient="linear(135deg, #3730a3 0%, #1e3a8a 62%, #115e59 100%)"
          border="1px solid rgba(165,180,252,0.48)"
          boxShadow="0 24px 72px rgba(67,56,202,0.26)"
        >
          <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={8} alignItems="center">
            <VStack align="start" spacing={4}>
              <Text color="teal.200" fontSize="sm" fontWeight="700" textTransform="uppercase" letterSpacing="0.08em">
                {copy.ctaBadge}
              </Text>
              <Heading fontSize={{ base: '3xl', md: '4xl' }}>
                {copy.ctaTitle}
              </Heading>
              <Text color="white" fontSize="lg" opacity={0.98}>
                {copy.ctaBody}
              </Text>
            </VStack>
            <VStack align="stretch" spacing={4}>
              <Button
                as={RouterLink}
                to="/login?tab=register"
                size="lg"
                bg="teal.300"
                color="gray.900"
                border="2px solid"
                borderColor="whiteAlpha.900"
                boxShadow="0 10px 24px rgba(20,184,166,0.45)"
                _hover={{ bg: 'teal.200', transform: 'translateY(-1px)' }}
                _active={{ bg: 'teal.400' }}
              >
                {copy.register}
              </Button>
              <Button as={RouterLink} to="/login" size="lg" variant="outline" borderColor="whiteAlpha.900" color="white" _hover={{ bg: 'whiteAlpha.300' }}>
                {copy.ctaSecondary}
              </Button>
            </VStack>
          </SimpleGrid>
        </Box>
      </Container>

      <Container maxW="7xl" px={{ base: 5, md: 8 }} py={{ base: 16, md: 20 }}>
        <VStack spacing={{ base: 8, md: 12 }} align="stretch">
          <Box
            p={{ base: 6, md: 7 }}
            borderRadius="2xl"
            bg="linear-gradient(90deg, rgba(8,47,73,0.45) 0%, rgba(15,23,42,0.65) 100%)"
            border="1px solid rgba(45,212,191,0.26)"
          >
            <Flex
              direction={{ base: 'column', lg: 'row' }}
              align={{ base: 'start', lg: 'center' }}
              justify="space-between"
              gap={4}
            >
              <VStack align="start" spacing={2}>
                <Badge colorScheme="cyan" variant="subtle">{copy.poweredBadge}</Badge>
                <Heading size="md">{copy.poweredTitle}</Heading>
                <Text color="gray.200">{copy.poweredBody}</Text>
              </VStack>

              <Button
                as={Link}
                href="https://tzusecurity.com"
                isExternal
                variant="outline"
                borderColor="teal.300"
                color="teal.200"
                _hover={{ bg: 'teal.900', color: 'teal.100' }}
              >
                {copy.poweredLink}
              </Button>
            </Flex>
          </Box>

          <Box
            id="contact"
            p={{ base: 7, md: 9 }}
            borderRadius="3xl"
            bg="gray.900"
            border="1px solid rgba(20,184,166,0.35)"
            boxShadow="0 22px 56px rgba(20,184,166,0.18)"
            maxW="4xl"
            w="full"
            mx="auto"
          >
            <VStack align="start" spacing={4}>
              <Heading size="lg">{copy.contactTitle}</Heading>
              <Text color="gray.300">{copy.contactBody}</Text>

              <Box as="form" onSubmit={handleContactSubmit} w="full">
                <VStack spacing={4} align="stretch">
                  <FormControl isRequired>
                    <FormLabel>{copy.contactName}</FormLabel>
                    <Input value={contactForm.name} onChange={onFormFieldChange('name')} bg="whiteAlpha.100" borderColor="whiteAlpha.300" />
                  </FormControl>

                  <FormControl isRequired>
                    <FormLabel>{copy.contactEmail}</FormLabel>
                    <Input type="email" value={contactForm.email} onChange={onFormFieldChange('email')} bg="whiteAlpha.100" borderColor="whiteAlpha.300" />
                  </FormControl>

                  <FormControl>
                    <FormLabel>{copy.contactCompany}</FormLabel>
                    <Input value={contactForm.company} onChange={onFormFieldChange('company')} bg="whiteAlpha.100" borderColor="whiteAlpha.300" />
                  </FormControl>

                  <FormControl>
                    <FormLabel>{copy.contactSource}</FormLabel>
                    <Select
                      value={contactForm.source}
                      onChange={onFormFieldChange('source')}
                      placeholder={copy.contactSourcePlaceholder}
                      bg="whiteAlpha.100"
                      borderColor="whiteAlpha.300"
                    >
                      {copy.contactSourceOptions.map((option) => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl isRequired>
                    <FormLabel>{copy.contactMessage}</FormLabel>
                    <Textarea value={contactForm.message} onChange={onFormFieldChange('message')} minH="120px" bg="whiteAlpha.100" borderColor="whiteAlpha.300" />
                  </FormControl>

                  <FormControl display="none" aria-hidden="true">
                    <FormLabel>Website</FormLabel>
                    <Input
                      tabIndex={-1}
                      autoComplete="off"
                      value={contactForm.website}
                      onChange={onFormFieldChange('website')}
                    />
                  </FormControl>

                  <Button type="submit" bg="teal.300" color="gray.900" _hover={{ bg: 'teal.200' }}>
                    {isSubmittingContact ? copy.contactSubmitting : copy.contactSubmit}
                  </Button>
                </VStack>
              </Box>
            </VStack>
          </Box>
        </VStack>
      </Container>

      <Box
        as="footer"
        borderTop="1px solid rgba(148,163,184,0.12)"
        bg="gray.950"
        py={{ base: 6, md: 7 }}
      >
        <Container maxW="7xl" px={{ base: 5, md: 8 }}>
          <Text color="gray.400" fontSize="sm" textAlign="center">
            © {currentYear} ThreatModeling.studio. {copy.footerCopyright}
          </Text>
        </Container>
      </Box>
    </Box>
  );
};

export default LandingPage;